// Single source of truth for how a movement's match status is classified and
// displayed across the app (Reconciliation page, Executive Summary, financial
// table). Mirrors the backend SETTLED set in functions/_lib/reconciliation.ts.
import { CHART } from "./chart";
import type { MatchStatus } from "@shared/types";

// The status given to financing movements that intentionally have no receipt.
// These are NOT "unreconciled" — they are settled financial movements by design.
export const FINANCING_STATUS = "Financing — no receipt";

// Movements in these statuses need no further action ("settled").
export const SETTLED = new Set<string>(["Reconciled", FINANCING_STATUS]);

// The status options offered in the assign dropdown.
export const STATUS_OPTIONS: MatchStatus[] = [
  "Reconciled",
  "Missing Support",
  "Vendor match — amount differs (review)",
  "Variance",
  FINANCING_STATUS,
];

export type ReconClass = "reconciled" | "financing" | "review";

// Three-way bucket: reconciled (done), financing (settled, no receipt by design),
// or review (everything still pending).
export function classify(matchStatus: string): ReconClass {
  if (matchStatus === FINANCING_STATUS) return "financing";
  if (SETTLED.has(matchStatus)) return "reconciled";
  return "review";
}

export function isFinancing(matchStatus: string): boolean {
  return matchStatus === FINANCING_STATUS;
}

// Short, friendly labels for the three classes (used in charts/legends).
export const CLASS_LABEL: Record<ReconClass, string> = {
  reconciled: "Reconciled",
  financing: "Financing (no receipt)",
  review: "Needs review",
};

export const CLASS_COLOR: Record<ReconClass, string> = {
  reconciled: CHART.positive,
  financing: CHART.axis, // neutral gray — settled, not a problem
  review: CHART.warn,
};

// Per-status pie colors (keeps each review sub-status distinguishable).
export const STATUS_COLORS: Record<string, string> = {
  Reconciled: CHART.positive,
  [FINANCING_STATUS]: CHART.axis,
  "Vendor match — amount differs (review)": CHART.warn,
  Variance: CHART.warn,
  "Missing Support": CHART.critical,
};
